/**
 * Language filter for live news headlines.
 *
 * Wire services (PR Newswire, GlobeNewswire, etc.) frequently syndicate
 * foreign-language press releases (French, German, Spanish, Italian, Dutch,
 * Portuguese, Indonesian, Malay, Chinese, Japanese, Korean, Hebrew, etc.)
 * for US-listed companies. This filter detects and rejects non-English
 * headlines so news tiles display only English content.
 */

// Non-Latin writing systems (Chinese, Japanese, Korean, Hebrew, Arabic, Cyrillic, Thai, Greek, etc.)
const NON_LATIN_RE =
  /[\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0E00-\u0E7F\u1100-\u11FF\u3040-\u30FF\u3130-\u318F\u3400-\u4DBF\u4E00-\u9FFF\uAC00-\uD7AF]/;

// Common URL language path segments used by wire feeds (GlobeNewswire, PR Newswire, etc.)
const FOREIGN_URL_LANG_RE =
  /\/(?:news-release|press-release|article|news|releases)\/.*?\/(?:pt|ms|id|th|es|fr|de|nl|it|ja|ko|zh|zh-cn|zh-tw|zh-hk|ar|he|ru|pl|vi|tr|sv|da|no|fi)\//i;

// Characters unique to Portuguese (and foreign languages) that never appear in English words:
// ã, õ (e.g. gestão, instalações, milhões)
const PORTUGUESE_CHAR_RE = /[ãõ]/i;

// Single high-confidence foreign multi-word phrases or grammatical constructs
const FOREIGN_PHRASE_RE = new RegExp(
  '\\b(' +
  // French phrases & contractions
  "[dl]['’](?:environ|actions|exercice|achat|un|une|année|activités|est|activité|unité|administration|investissement)|" +
  "par rapport à|au cours de|à l'occasion|droits de vote|capital social|" +
  "déclare un dividende|déclare des dividendes|résultats financiers|chiffre d'affaires|" +
  "troisième trimestre|quatrième trimestre|premier trimestre|deuxième trimestre|" +
  "conférence investisseurs|conférences investisseurs|assemblée générale|conseil d'administration|" +
  "relative au nombre|relatif au nombre|relative aux|relatif aux|" +
  "fait le point sur|point d['’]activité|fait état du|annoncent un projet|" +
  // German phrases & constructs
  "gibt bekannt|geben bekannt|gab bekannt|im Wert von|darunter Beteiligungen|" +
  "sowie mehr als|und informiert über|auf Basis von|zum Start einer|Ergebnisse für|" +
  "Geschäftsjahr|Gesamtbestand|Aufsichtsrat|Aufsichtsrats|Vorstandsvorsitzender|" +
  "mögliche Fusion|über den aktuellen|im Rahmen des|" +
  // Dutch phrases
  "meldt totale|bezittingen van|waaronder belangen|meer dan|en bijna|" +
  // Italian phrases
  "comunica attività|rende noto|per circa|comprendenti partecipazioni|in modo da|" +
  "consiglio di amministrazione|assemblea degli azionisti|" +
  // Spanish / Portuguese phrases
  "informa de que|reporta una tenencia|sus activos totales|ascienden a aproximadamente|" +
  "estados financieros|ejercicio fiscal|acciones ordinarias|junta general|asamblea general|" +
  "fecha contratos|no valor de|obtiene contratos|por valor de|" +
  // Indonesian / Malay phrases
  "mendapatkan kontrak|memperoleh kontrak|kontrak baru|kontrak baharu|beberapa tahun|untuk tempoh" +
  ')\\b',
  'i'
);

// High-confidence foreign vocabulary / stopwords. If a headline matches >= 2 of these distinct words,
// it is non-English.
const FOREIGN_STOPWORDS = new Set([
  // German
  'und', 'der', 'die', 'das', 'den', 'dem', 'des', 'für', 'von', 'mit', 'nach', 'über', 'aus', 'bei', 'nicht', 'eine', 'einer', 'einem', 'einen', 'eines', 'sowie', 'bekannt', 'ergebnisse', 'geschäftsjahr', 'quartal',
  // French
  'les', 'des', 'dans', 'pour', 'sur', 'avec', 'aux', 'cette', 'ces', 'qui', 'déclare', 'dévoile', 'annonce', 'annoncent', 'résultats', 'trimestre', 'trimestriel', 'actionnaires', 'société', 'ententes', 'organise', 'visant',
  // Spanish / Portuguese
  'los', 'las', 'unos', 'unas', 'del', 'por', 'para', 'sus', 'sobre', 'más', 'informa', 'anuncia', 'acciones', 'ejercicio', 'tenencia', 'millones', 'milhões', 'adquisición', 'amplía', 'adquirirá', 'contratos', 'serviços', 'gestão', 'instalações', 'plurianuais',
  // Italian
  'dello', 'della', 'dei', 'degli', 'delle', 'comunica', 'attività', 'notizie', 'risultati', 'capacità', 'crescita', 'servizio',
  // Dutch
  'van', 'het', 'een', 'voor', 'naar', 'waaronder', 'belangen', 'bezittingen', 'meldt', 'miljoen', 'breidt', 'dienstverlening',
  // Scandinavian
  'och', 'att', 'det', 'som', 'för', 'till', 'från',
  // Indonesian & Malay
  'mendapatkan', 'memperoleh', 'kontrak', 'baharu', 'perkhidmatan', 'fasilitas', 'fasiliti', 'multitahun', 'senilai', 'bernilai', 'juta', 'tempoh', 'beberapa', 'tahun', 'singapura', 'layanan'
]);

/** Returns true if the headline appears to be non-English. */
export function isNonEnglishHeadline(headline: string | null | undefined, url?: string | null): boolean {
  if (!headline) return false;
  // 0. Language prefix embedded in wire URL (GlobeNewswire / PR Newswire / etc.)
  if (url && FOREIGN_URL_LANG_RE.test(url)) return true;
  // 1. Non-Latin scripts (Chinese, Japanese, Korean, Hebrew, Arabic, Cyrillic, Thai, etc.)
  if (NON_LATIN_RE.test(headline)) return true;
  // 2. Language-specific unique characters (Portuguese ã, õ)
  if (PORTUGUESE_CHAR_RE.test(headline)) return true;
  // 3. High-confidence multi-word grammatical constructs / phrases
  if (FOREIGN_PHRASE_RE.test(headline)) return true;
  // 4. Count distinct foreign stopword / vocabulary occurrences
  const words = headline.toLowerCase().replace(/[^\p{L}\s]/gu, ' ').split(/\s+/).filter(Boolean);
  let foreignCount = 0;
  const seen = new Set<string>();
  for (const w of words) {
    if (FOREIGN_STOPWORDS.has(w) && !seen.has(w)) {
      seen.add(w);
      foreignCount++;
      if (foreignCount >= 2) return true;
    }
  }
  return false;
}

/** Returns true if the headline is English (or empty/null). */
export function isEnglishHeadline(headline: string | null | undefined, url?: string | null): boolean {
  return !isNonEnglishHeadline(headline, url);
}
